import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Gem, Bomb } from "lucide-react";
import GameShell from "@/components/GameShell";
import BetControls from "@/components/BetControls";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";

// Fair preview multiplier (1% edge) for picks of 25-grid
const C = (n, k) => {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  let r = 1;
  for (let i = 0; i < k; i++) r = (r * (n - i)) / (i + 1);
  return r;
};
const multFor = (mines, picks) => {
  if (picks <= 0) return 1;
  const safe = 25 - mines;
  if (picks > safe) return 0;
  const p = C(safe, picks) / C(25, picks);
  return (0.99 / p).toFixed(4);
};

export default function Mines() {
  const { setUser, user } = useAuth();
  const { currency } = useCurrency();
  const [amount, setAmount] = useState(10);
  const [mines, setMines] = useState(3);
  const [picked, setPicked] = useState([]); // 0..24
  const [result, setResult] = useState(null); // {mines_positions, picks, hit, multiplier, payout}
  const [busy, setBusy] = useState(false);

  const multiplier = useMemo(() => multFor(mines, picked.length), [mines, picked.length]);
  const potential = useMemo(() => {
    const m = Number(multiplier);
    return Number.isFinite(m) ? Number(amount) * m : 0;
  }, [amount, multiplier]);

  const togglePick = (i) => {
    if (busy) return;
    setResult(null);
    setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i].slice(0, 24 - mines + 1)));
  };

  const clear = () => {
    setPicked([]);
    setResult(null);
  };

  const placeBet = async (betAmount, quiet = false) => {
    if (picked.length === 0) {
      toast.error("Pick at least one tile");
      return null;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/games/mines", {
        amount: Number(betAmount),
        currency,
        mines,
        picks: picked,
      });
      setResult({ ...data.bet.result, payout: data.bet.payout, multiplier: data.bet.multiplier, bet: Number(betAmount) });
      setUser((u) => ({ ...u, balances: data.balances }));
      if (!quiet) {
        if (data.bet.profit > 0) toast.success(`×${data.bet.multiplier} · +${formatAmount(data.bet.profit, currency)}`);
        else toast.error("Boom.");
      }
      return data.bet;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const play = async () => {
    if (!user) return;
    if (Number(amount) <= 0) return toast.error("Enter a bet");
    await placeBet(amount);
  };

  const tileState = (i) => {
    if (!result) return picked.includes(i) ? "picked" : "idle";
    if (result.mines_positions?.includes(i)) return picked.includes(i) ? "busted" : "mine";
    if (picked.includes(i)) return "safe";
    return "idle";
  };

  return (
    <GameShell title="Mines" subtitle="Reveal tiles. Avoid the blast. Cash out safe." accent="#FF3366"
      aside={
        <BetControls
          amount={amount}
          setAmount={setAmount}
          onPlay={play}
          autoPlaceBet={(a) => placeBet(a, true)}
          busy={busy}
          disabled={!user || busy || picked.length === 0}
          playLabel={result ? "Play Again" : "Reveal Tiles"}
          extra={
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Mines</span>
                  <span className="font-mono-num text-amber-400 text-sm">{mines}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="24"
                  value={mines}
                  onChange={(e) => setMines(Number(e.target.value))}
                  disabled={picked.length > 0}
                  className="w-full"
                  data-testid="mines-count"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="panel py-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Picks</div>
                  <div className="font-mono-num text-xl font-bold" data-testid="mines-picks">{picked.length}</div>
                </div>
                <div className="panel py-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Multiplier</div>
                  <div className="font-mono-num text-xl font-bold text-amber-400" data-testid="mines-multiplier">×{multiplier}</div>
                </div>
              </div>
              <div className="panel py-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Potential Payout</div>
                <div className="font-mono-num text-lg font-bold text-emerald-400">
                  {formatAmount(potential, currency)} <span className="text-zinc-500">{currency}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button onClick={clear} disabled={busy || picked.length === 0} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-rose-400 disabled:opacity-40" data-testid="mines-reset">
                  Clear picks
                </button>
                {result && (
                  <span className={`text-xs font-mono-num ${result.hit ? "text-rose-400" : "text-emerald-400"}`} data-testid="mines-result">
                    {result.hit ? "Hit a mine" : `+${formatAmount(result.payout - result.bet, currency)}`}
                  </span>
                )}
              </div>
            </div>
          }
        />
      }
    >
      <div className="h-[460px] flex items-center justify-center">
        <div className="grid grid-cols-5 gap-2 w-full max-w-md">
          {Array.from({ length: 25 }).map((_, i) => {
            const s = tileState(i);
            return (
              <div
                key={i}
                onClick={() => togglePick(i)}
                className={`mine-tile ${s === "safe" ? "revealed safe" : ""} ${s === "mine" || s === "busted" ? "revealed mine" : ""} ${s === "busted" ? "busted" : ""}`}
                data-testid={`mine-tile-${i}`}
                data-state={s}
                style={s === "picked" ? { borderColor: "rgba(0,240,255,0.5)", background: "rgba(0,240,255,0.08)" } : {}}
              >
                {s === "safe" && <Gem size={18} className="text-emerald-400" />}
                {(s === "mine" || s === "busted") && <Bomb size={18} className="text-rose-400" />}
                {s === "picked" && <span className="w-2 h-2 rounded-full bg-cyan-400" />}
              </div>
            );
          })}
        </div>
      </div>
    </GameShell>
  );
}
